// Prise de photo (appareil arrière du téléphone) et disponibilité de la lecture par l'assistance.
// Bouton toujours visible : désactivé avec son explication si l'assistance manque ou si l'appareil est hors connexion.
import { useRef, useId, type ReactNode } from 'react';
import { useSession } from '../lib/session.tsx';
import { useEnLigne } from '../lib/preferences.ts';
import { Bouton } from './ui.tsx';

export interface Plaque {
  fabricant: string | null; gamme: string | null; reference: string | null;
  versions: { materiel: string | null; logiciel: string | null; firmware: string | null };
  numero_serie: string | null; autres: { libelle: string; valeur: string }[]; illisible: string[];
}
export interface Afficheur {
  message: string | null; codes: string[]; voyants: { libelle: string; etat: string }[]; symptome: string | null; illisible: string[];
}

/** La lecture d'une photo par l'assistance est-elle possible maintenant ? Sinon, pourquoi (texte affiché à côté du bouton). */
export function useLecturePhoto(): { possible: boolean; raison: string | null; enLigne: boolean } {
  const { etat } = useSession();
  const enLigne = useEnLigne();
  if (!enLigne) return { possible: false, raison: 'Lecture de photo indisponible hors connexion : elle nécessite le réseau.', enLigne };
  if (!etat) return { possible: false, raison: 'Vérification de l’assistance en cours…', enLigne };
  if (!etat.ia.disponible) return { possible: false, raison: 'Lecture automatique indisponible : l’assistance Claude n’est pas configurée (Administration › Assistance IA). Saisissez les valeurs à la main.', enLigne };
  if (!etat.ia.documentsClientsAutorises) return { possible: false, raison: 'Lecture automatique indisponible : l’envoi des photos à l’assistance n’est pas autorisé (Administration › Assistance IA). Saisissez les valeurs à la main.', enLigne };
  return { possible: true, raison: null, enLigne };
}

/**
 * Bouton qui ouvre l'appareil photo (ou la galerie) : input fichier caché, accept="image/*", capture="environment".
 * Le bouton porte le libellé ; l'explication d'indisponibilité lui est reliée (aria-describedby).
 */
export function BoutonPhoto({ children, icone, surPhoto, desactive, explication, decritPar, chargement, variante = 'secondaire', className }: {
  children: ReactNode; icone?: ReactNode; surPhoto: (f: File) => void; desactive?: boolean; explication?: string | null;
  /** Identifiant d'une explication affichée ailleurs (une seule pour plusieurs boutons). */
  decritPar?: string; chargement?: boolean; variante?: 'principal' | 'secondaire' | 'fantome'; className?: string;
}) {
  const entree = useRef<HTMLInputElement>(null);
  const id = useId();
  return (
    <span className={`photo-bouton ${className ?? ''}`}>
      <Bouton variante={variante} icone={icone} chargement={chargement} disabled={desactive}
        aria-describedby={explication ? `${id}-x` : decritPar} onClick={() => entree.current?.click()}>{children}</Bouton>
      <input ref={entree} type="file" accept="image/*" capture="environment" hidden tabIndex={-1} aria-hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) surPhoto(f); }} />
      {explication && <span id={`${id}-x`} className="tres-petit discret photo-explication">{explication}</span>}
    </span>
  );
}
